import React, { forwardRef, Children } from 'react'
import PropTypes from 'prop-types'
import { Observable } from '@fs/zion-frontend-friends'
import Ghost from '../ghost'
import Row from '../row'
import { useContainerWidth, gutters as responsiveGutter } from '../responsive'

const partitionSize = 10

export function useHorizontalFlowGrid(flowGridRef) {
  const queryItemByIndex = React.useCallback(
    (index) => {
      const partitionIndex = Math.floor(index / partitionSize)
      const itemIndex = index % partitionSize

      const partition = flowGridRef.current.children[partitionIndex].children[0]
      let item = null
      if (partition.children.length) {
        item = partition.children[0].children[itemIndex]
      }
      return {
        partition: { node: partition, index: partitionIndex },
        item: { node: item, index: itemIndex },
        typicalPartitionSize: partitionSize,
      }
    },
    [flowGridRef]
  )
  return queryItemByIndex
}

function partitionItems(children) {
  return children.reduce((itemPartition, item, index) => {
    const needsNewPartition = index % partitionSize === 0
    if (needsNewPartition) itemPartition.push([])
    const partition = itemPartition[itemPartition.length - 1]
    partition.push(item)
    return itemPartition
  }, [])
}

export const HorizontalFlowGrid = forwardRef(
  ({ children, gutters = 'sm', keepVisible, widthEstimate, ...props }, ref) => {
    const atWidth = useContainerWidth()
    const losNiños = Children.toArray(children)
    const partitions = partitionItems(losNiños)
    const currentGutter = atWidth(responsiveGutter[gutters])
    const gutterWidthAddition = currentGutter * 2 * partitionSize

    return (
      <Observable {...props}>
        <Row ref={ref} gutters={gutters}>
          {partitions.map((partition, index) => (
            <Ghost
              noShrinkX
              key={index} // eslint-disable-line react/no-array-index-key -- No other conceivable solution
              widthEstimate={widthEstimate * partitionSize + gutterWidthAddition}
              keepVisible={keepVisible}
            >
              <Row gutters={gutters}>
                {partition.map((item) => (
                  <Ghost noShrinkX key={item.key} widthEstimate={widthEstimate} keepVisible={keepVisible}>
                    {item}
                  </Ghost>
                ))}
              </Row>
            </Ghost>
          ))}
        </Row>
      </Observable>
    )
  }
)

HorizontalFlowGrid.propTypes = {
  /** Array of items to be displayed */
  children: PropTypes.node,

  /** Spacing between items */
  gutters: PropTypes.oneOf(['xxl', 'xl', 'lg', 'md', 'sm', 'xs', 'xxs', 'nano', 'pico']),

  /** Allow Ghost contents to remain in memory to avoid being loaded again. */
  keepVisible: PropTypes.bool,

  /** Width estimate of each child */
  widthEstimate: PropTypes.number,
}

export default HorizontalFlowGrid
